import React, { useMemo } from "react";

import { type Dashboard } from "../../Dashboards/hook/api/dashboard";
import { createSummaryDashboard } from "../Navigation/utils";
import type { SummaryDashboard } from "../../../../api/useSummaryDashboards";
import { useSettings } from "../../../../api/useSettings";

export function usePositionSummaryDashbardTabs(
  projectId: string,
  remoteDashboards: Dashboard[],
  rawSummaryDashboards: SummaryDashboard[],
) {
  const settings = useSettings();

  const remoteSummaryDashboards = React.useMemo(
    () => rawSummaryDashboards.map(({ createdAt, updatedAt, ...rest }) => rest),
    [rawSummaryDashboards],
  );

  const publishedDashboards = React.useMemo(
    () => remoteDashboards.filter(d => d.published),
    [remoteDashboards],
  );

  const summaryPos = React.useMemo(() => {
    if (settings.HIDE_LEGACY_SUMMARY_PAGE) return -1;
    const positions = remoteSummaryDashboards.map(o => o.position).sort((a, b) => a - b);
    const len = positions.length;
    const first = positions[0];

    if (len === 0) return 0;
    if (first !== undefined && first > 0) return 0;

    for (let i = 1; i < len; i++) {
      const prev = positions[i - 1];
      const curr = positions[i];
      if (curr !== undefined && prev !== undefined && curr - prev >= 2) return prev + 1;
    }

    const last = positions[len - 1];
    return last === undefined ? 0 : last + 1;
  }, [remoteSummaryDashboards, settings.HIDE_LEGACY_SUMMARY_PAGE]);

  const positionSummaryDashboards = React.useMemo(() => {
    const dashMap = new Map(publishedDashboards.map(d => [d.id, d]));
    const dashboardsWithPos: { dashboard: Dashboard; pos: number }[] = [];

    for (const occupant of remoteSummaryDashboards) {
      const dashboard = dashMap.get(occupant.dashboardId);
      if (dashboard) dashboardsWithPos.push({ dashboard, pos: occupant.position });
    }

    if (!settings.HIDE_LEGACY_SUMMARY_PAGE) {
      dashboardsWithPos.push({ dashboard: createSummaryDashboard(projectId), pos: summaryPos });
    }
    dashboardsWithPos.sort((a, b) => a.pos - b.pos);

    return dashboardsWithPos.map(({ dashboard, pos }) => ({ ...dashboard, pos }));
  }, [
    settings.HIDE_LEGACY_SUMMARY_PAGE,
    publishedDashboards,
    remoteSummaryDashboards,
    summaryPos,
    projectId,
  ]);

  return useMemo(
    () => ({
      summaryPos,
      positionSummaryDashboards,
    }),
    [summaryPos, positionSummaryDashboards],
  );
}
